import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { addDays, format } from "date-fns";
import "./LaunchCalendar.css";
import { fetchRocketFamilies } from "../data/rocketFamilies";
import { fetchPads } from "../data/pads";
import { getCalendarInformation } from "../data/stats";
import { changeTimeToLocal, formatTime, parseUtcMs } from "../utils";
import Filter from "../multipurpose/Filter.js";

// The all-years view is a day-of-month index, not a weekday calendar.
// 2024 keeps February 29 on the plate.
const LEAP_ANCHOR = 2024;
const WEEKDAYS = [
  ["Sun", "S"],
  ["Mon", "M"],
  ["Tue", "T"],
  ["Wed", "W"],
  ["Thu", "T"],
  ["Fri", "F"],
  ["Sat", "S"],
];

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

const cx = (...parts) => parts.filter(Boolean).join(" ");

const launchDate = (launch) => {
  const date = new Date(launch.time);
  return Number.isNaN(date.getTime()) ? null : date;
};

const rocketAccent = (rocket) => {
  const name = String(rocket?.name || "").toLowerCase();
  if (name.includes("falcon 1")) return "#e8a317";
  if (rocket?.color) return rocket.color;
  if (name.includes("starship") || name.includes("super heavy")) return "#8067d8";
  if (name.includes("falcon heavy")) return "#2563b8";
  if (name.includes("falcon")) return "#238bb8";
  if (name.includes("dragon")) return "#168f7d";
  return "#8b5cf6";
};

const outcomeLabel = (outcome) =>
  String(outcome || "TBD")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .replace("Tbd", "TBD");

const outcomeClass = (outcome) => {
  const name = String(outcome || "").toLowerCase();
  if (name.includes("success")) return "success";
  if (name.includes("partial")) return "partial";
  if (name.includes("fail")) return "failure";
  return "tbd";
};

const stageLabel = (launch) => {
  if (typeof launch.stages_string === "string" && launch.stages_string.trim()) {
    return launch.stages_string.trim();
  }

  const recoveries = Array.isArray(launch.stage_and_recoveries)
    ? launch.stage_and_recoveries
    : Array.isArray(launch.recoveries)
      ? launch.recoveries
      : [];
  const names = recoveries.map((entry) => entry?.stage?.nickname || entry?.stage?.name).filter(Boolean);
  return names.length ? names.join(", ") : "";
};

const formatClock = (time) => {
  const ms = parseUtcMs(time);
  if (Number.isNaN(ms)) return "";
  const date = new Date(ms);
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  return `${hours}:${minutes} UTC`;
};

const formatWeekday = (time) => {
  const ms = parseUtcMs(time);
  if (Number.isNaN(ms)) return "";
  return new Intl.DateTimeFormat("en-US", { weekday: "long", timeZone: "UTC" }).format(new Date(ms));
};

const prefersReduce = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const withOther = (rows, limit = 5) => {
  if (rows.length <= limit) return rows;
  const head = rows.slice(0, limit - 1);
  const count = rows.slice(limit - 1).reduce((sum, row) => sum + row.count, 0);
  return [...head, { name: "Other", count, color: "rgba(154, 164, 181, 0.55)" }];
};

const Chevron = ({ direction }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <path
      d={direction === "left" ? "M9 2.5 L4.5 7 L9 11.5" : "M5 2.5 L9.5 7 L5 11.5"}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
    />
  </svg>
);

const KIND_ORDER = ["falcon9", "heavy", "falcon1", "starship", "other"];

const VEHICLE_LABEL = {
  falcon9: "Falcon 9",
  heavy: "Falcon Heavy",
  falcon1: "Falcon 1",
  starship: "Starship",
  other: "Other",
};

const VEHICLE_PILL = {
  falcon9: "F9",
  heavy: "FH",
  falcon1: "F1",
  starship: "Starship",
  other: "Other",
};

const vehicleKind = (rocket) => {
  const name = String(rocket?.name || "").toLowerCase();
  if (name.includes("starship") || name.includes("super heavy")) return "starship";
  if (name.includes("heavy")) return "heavy";
  if (name.includes("falcon 1")) return "falcon1";
  if (name.includes("falcon")) return "falcon9";
  return "other";
};

const dayMarks = (flights, rocketById) => {
  const tally = new Map();
  flights.forEach((launch) => {
    const rocket = rocketById.get(String(launch.rocket));
    const kind = vehicleKind(rocket);
    const prev = tally.get(kind);
    if (prev) prev.count += 1;
    else tally.set(kind, { kind, count: 1, color: rocketAccent(rocket) });
  });
  return [...tally.values()].sort(
    (a, b) => b.count - a.count || KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind)
  );
};

const LaunchCalendar = () => {
  const initFilter = () => {
    const params = new URLSearchParams(window.location.search);
    const filterParam = params.get("filter");
    if (!filterParam) return null;
    try {
      return JSON.parse(decodeURIComponent(filterParam));
    } catch (error) {
      console.error("Error parsing filter from URL:", error);
      return null;
    }
  };

  const now = new Date();
  const stageRef = useRef(null);
  const intelRef = useRef(null);
  const railRef = useRef(null);
  const dossierRef = useRef(null);
  const actionRef = useRef({});

  const [rocketFamilies, setRocketFamilies] = useState([]);
  const [rockets, setRockets] = useState([]);
  const [pads, setPads] = useState([]);
  const [launches, setLaunches] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState(initFilter);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(now.getMonth());
  const [activeYear, setActiveYear] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    () => new Date(LEAP_ANCHOR, now.getMonth(), now.getDate())
  );

  const filterNames = { rocket__family: "Rocket Family" };
  const objectsData = { rocket__family: rocketFamilies, rocket: rockets };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rocketFamilyData, padData] = await Promise.all([fetchRocketFamilies(), fetchPads("", {})]);
        if (cancelled) return;
        const families = Array.isArray(rocketFamilyData) ? rocketFamilyData : [];
        setRocketFamilies(families);
        setRockets(
          families.flatMap((family) =>
            (family.rockets || []).map((rocket) => ({ ...rocket, familyId: family.id }))
          )
        );
        setPads(Array.isArray(padData) ? padData : []);
      } catch (error) {
        if (!cancelled) setLoading(false);
      } finally {
        if (!cancelled) setDataLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (filter || !dataLoaded) return undefined;
    const initialRocketFamilyFilter = rocketFamilies.reduce((acc, family) => {
      acc[family.id] = {
        hide__rocket: Object.fromEntries((family.rockets || []).map((rocket) => [rocket.id, true])),
      };
      return acc;
    }, {});
    setFilter({ rocket__family: initialRocketFamilyFilter });
    return undefined;
  }, [dataLoaded, filter, rocketFamilies]);

  useEffect(() => {
    if (!filter || !dataLoaded) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const calendarStatsData = await getCalendarInformation(filter);
        if (cancelled) return;
        setStats(calendarStatsData || {});
        setLaunches(changeTimeToLocal(calendarStatsData?.launches || []));
      } catch (error) {
        if (!cancelled) {
          setStats({});
          setLaunches([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filter, dataLoaded]);

  const rocketById = useMemo(() => {
    const map = new Map();
    rockets.forEach((rocket) => map.set(String(rocket.id), rocket));
    return map;
  }, [rockets]);

  const padById = useMemo(() => {
    const map = new Map();
    pads.forEach((pad) => map.set(String(pad.id), pad));
    return map;
  }, [pads]);

  const monthAnchor = useMemo(
    () => new Date(activeYear ?? LEAP_ANCHOR, currentMonth, 1),
    [activeYear, currentMonth]
  );
  const monthName = format(monthAnchor, "MMMM");
  const daysInMonth = new Date(activeYear ?? LEAP_ANCHOR, currentMonth + 1, 0).getDate();

  const monthAll = useMemo(
    () => launches.filter((launch) => launchDate(launch)?.getMonth() === currentMonth),
    [launches, currentMonth]
  );

  const yearSpan = useMemo(() => {
    const counts = new Map();
    monthAll.forEach((launch) => {
      const year = launchDate(launch).getFullYear();
      counts.set(year, (counts.get(year) || 0) + 1);
    });
    // Keep the selected year visible when this month has none of its flights.
    if (activeYear != null && !counts.has(activeYear)) counts.set(activeYear, 0);
    return [...counts.keys()]
      .sort((a, b) => a - b)
      .map((year) => ({ year, count: counts.get(year) }));
  }, [monthAll, activeYear]);

  const byDay = useMemo(() => {
    const map = new Map();
    monthAll.forEach((launch) => {
      const date = launchDate(launch);
      if (activeYear != null && date.getFullYear() !== activeYear) return;
      const day = date.getDate();
      const list = map.get(day);
      if (list) list.push(launch);
      else map.set(day, [launch]);
    });
    map.forEach((list) => list.sort((a, b) => launchDate(b) - launchDate(a)));
    return map;
  }, [monthAll, activeYear]);

  const monthLaunches = useMemo(() => {
    const list = [];
    byDay.forEach((items) => list.push(...items));
    return list;
  }, [byDay]);

  const maxCount = useMemo(() => {
    let max = 0;
    byDay.forEach((items) => {
      if (items.length > max) max = items.length;
    });
    return max;
  }, [byDay]);

  const busiestDays = useMemo(() => {
    const days = [];
    byDay.forEach((items, day) => {
      if (items.length === maxCount && maxCount > 0) days.push(day);
    });
    return days.sort((a, b) => a - b);
  }, [byDay, maxCount]);

  const yearMax = useMemo(
    () => yearSpan.reduce((max, item) => Math.max(max, item.count), 0),
    [yearSpan]
  );

  const mixFor = (pick) => {
    const map = new Map();
    monthLaunches.forEach((launch) => {
      const entry = pick(launch);
      const existing = map.get(entry.name);
      if (existing) existing.count += 1;
      else map.set(entry.name, { ...entry, count: 1 });
    });
    return [...map.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  };

  const vehicleMix = withOther(
    mixFor((launch) => {
      const rocket = rocketById.get(String(launch.rocket));
      return { name: rocket?.name || "Unknown", color: rocketAccent(rocket) };
    })
  );
  const padMix = withOther(
    mixFor((launch) => {
      const pad = padById.get(String(launch.pad));
      return { name: pad?.nickname || pad?.name || "Unknown", color: "var(--bt-cyan)" };
    }),
    4
  );

  const outcomeCounts = monthLaunches.reduce(
    (counts, launch) => {
      counts[outcomeClass(launch.launch_outcome)] += 1;
      return counts;
    },
    { success: 0, partial: 0, failure: 0, tbd: 0 }
  );
  const decided = outcomeCounts.success + outcomeCounts.partial + outcomeCounts.failure;
  const successRate = decided ? Math.round((outcomeCounts.success / decided) * 100) : null;
  const daysFlown = byDay.size;

  const cells = useMemo(() => {
    const anchor = activeYear ?? LEAP_ANCHOR;
    const first = new Date(anchor, currentMonth, 1);
    const count = new Date(anchor, currentMonth + 1, 0).getDate();
    const items = [];
    if (activeYear != null) {
      for (let index = 0; index < first.getDay(); index += 1) items.push(null);
    }
    for (let day = 1; day <= count; day += 1) items.push(new Date(anchor, currentMonth, day));
    while (items.length % 7 !== 0) items.push(null);
    return items;
  }, [activeYear, currentMonth]);

  const selectedLaunches = byDay.get(selectedDate.getDate()) || [];
  const selectedYears = [
    ...new Set(selectedLaunches.map((launch) => launchDate(launch).getFullYear())),
  ].sort((a, b) => b - a);

  const yearGroups = [];
  selectedLaunches.forEach((launch) => {
    const year = launchDate(launch).getFullYear();
    const last = yearGroups[yearGroups.length - 1];
    if (!last || last.year !== year) yearGroups.push({ year, flights: [launch] });
    else last.flights.push(launch);
  });

  const yearLine = (() => {
    if (!selectedYears.length) return "Nothing on this date in the current filter.";
    if (selectedYears.length === 1) return String(selectedYears[0]);
    return `${selectedYears[selectedYears.length - 1]} – ${selectedYears[0]} · ${plural(selectedYears.length, "year")}`;
  })();

  const revealDossier = () => {
    const dossier = dossierRef.current;
    if (!dossier) return;
    const rect = dossier.getBoundingClientRect();
    if (rect.top > window.innerHeight * 0.72) {
      dossier.scrollIntoView({ behavior: prefersReduce() ? "auto" : "smooth", block: "start" });
    }
  };

  const selectDay = (date) => {
    setSelectedDate(new Date(LEAP_ANCHOR, date.getMonth(), date.getDate()));
    revealDossier();
  };

  const selectYear = (year) => {
    setActiveYear(year);
    const dim = new Date(year ?? LEAP_ANCHOR, currentMonth + 1, 0).getDate();
    setSelectedDate((prev) => new Date(LEAP_ANCHOR, currentMonth, Math.min(prev.getDate(), dim)));
  };

  const shiftMonth = (delta) => {
    let month = currentMonth + delta;
    let year = activeYear;
    if (year != null) {
      if (month > 11) {
        month = 0;
        year += 1;
      } else if (month < 0) {
        month = 11;
        year -= 1;
      }
      setActiveYear(year);
    } else {
      month = (month + 12) % 12;
    }
    const dim = new Date(year ?? LEAP_ANCHOR, month + 1, 0).getDate();
    const day = Math.min(selectedDate.getDate(), dim);
    setCurrentMonth(month);
    setSelectedDate(new Date(LEAP_ANCHOR, month, day));
  };

  const moveDay = (delta) => {
    const anchor = activeYear ?? LEAP_ANCHOR;
    const next = addDays(new Date(anchor, currentMonth, selectedDate.getDate()), delta);
    if (activeYear != null) setActiveYear(next.getFullYear());
    setCurrentMonth(next.getMonth());
    setSelectedDate(new Date(LEAP_ANCHOR, next.getMonth(), next.getDate()));
    revealDossier();
  };

  const jumpToday = () => {
    const today = new Date();
    if (activeYear != null) setActiveYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
    setSelectedDate(new Date(LEAP_ANCHOR, today.getMonth(), today.getDate()));
    revealDossier();
  };

  actionRef.current = { moveDay };

  useEffect(() => {
    const onKey = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || event.target?.isContentEditable) return;
      if (event.target?.closest("a, button")) return;
      const delta = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[event.key];
      if (!delta) return;
      event.preventDefault();
      actionRef.current.moveDay(delta);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const rail = railRef.current;
    const active = rail?.querySelector(".lc-year.is-active");
    if (!rail || !active) return;
    const railRect = rail.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    if (activeRect.left >= railRect.left && activeRect.right <= railRect.right) return;
    const nextLeft = rail.scrollLeft + activeRect.left - railRect.left - railRect.width / 2 + activeRect.width / 2;
    rail.scrollTo({ left: nextLeft, behavior: prefersReduce() ? "auto" : "smooth" });
  }, [activeYear, yearSpan]);

  const showIntel = !loading && (monthLaunches.length > 0 || stats.num_days_with_launches != null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || prefersReduce()) return undefined;

    const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    const target = { px: 0, py: 0, mx: 72, my: 16, scroll: 0 };
    const current = { px: 0, py: 0, mx: 72, my: 16, scroll: 0 };
    let frame = 0;

    const tick = () => {
      frame = 0;
      current.px += (target.px - current.px) * 0.07;
      current.py += (target.py - current.py) * 0.07;
      current.mx += (target.mx - current.mx) * 0.16;
      current.my += (target.my - current.my) * 0.16;
      current.scroll += (target.scroll - current.scroll) * 0.08;
      stage.style.setProperty("--px", current.px.toFixed(4));
      stage.style.setProperty("--py", current.py.toFixed(4));
      stage.style.setProperty("--mx", `${current.mx.toFixed(2)}%`);
      stage.style.setProperty("--my", `${current.my.toFixed(2)}%`);
      stage.style.setProperty("--scroll", current.scroll.toFixed(3));
      const moving =
        Math.abs(target.px - current.px) > 0.0008 ||
        Math.abs(target.py - current.py) > 0.0008 ||
        Math.abs(target.mx - current.mx) > 0.05 ||
        Math.abs(target.my - current.my) > 0.05 ||
        Math.abs(target.scroll - current.scroll) > 0.05;
      if (moving) frame = requestAnimationFrame(tick);
    };

    const start = () => {
      if (!frame) frame = requestAnimationFrame(tick);
    };

    const onScroll = () => {
      const top = stage.getBoundingClientRect().top;
      target.scroll = Math.max(-36, Math.min(84, (88 - top) * 0.42));
      const intel = intelRef.current;
      if (intel) {
        const intelTop = intel.getBoundingClientRect().top;
        intel.style.setProperty("--drift", ((window.innerHeight * 0.55 - intelTop) * 0.08).toFixed(2));
      }
      start();
    };

    const onPointerMove = (event) => {
      if (!fine) return;
      const rect = stage.getBoundingClientRect();
      const width = rect.width || 1;
      const height = rect.height || 1;
      target.px = ((event.clientX - rect.left) / width) * 2 - 1;
      target.py = ((event.clientY - rect.top) / height) * 2 - 1;
      target.mx = ((event.clientX - rect.left) / width) * 100;
      target.my = ((event.clientY - rect.top) / height) * 100;
      start();
    };

    const onPointerLeave = () => {
      target.px = 0;
      target.py = 0;
      target.mx = 72;
      target.my = 16;
      start();
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    stage.addEventListener("pointermove", onPointerMove);
    stage.addEventListener("pointerleave", onPointerLeave);
    return () => {
      window.removeEventListener("scroll", onScroll);
      stage.removeEventListener("pointermove", onPointerMove);
      stage.removeEventListener("pointerleave", onPointerLeave);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [showIntel]);

  const today = new Date();
  const onToday =
    currentMonth === today.getMonth() &&
    selectedDate.getDate() === today.getDate() &&
    (activeYear == null || activeYear === today.getFullYear());

  const yearsOnPlate = activeYear != null ? 1 : new Set(monthAll.map((launch) => launchDate(launch).getFullYear())).size;
  const recordPct = stats.percentage_days_with_launches;

  const flightMeta = (launch) => {
    const rocket = rocketById.get(String(launch.rocket));
    const pad = padById.get(String(launch.pad));
    return [formatWeekday(launch.time), formatClock(launch.time), rocket?.name, pad?.nickname || pad?.name, stageLabel(launch)]
      .filter(Boolean)
      .join(" · ");
  };

  return (
    <div className="lc-page">
      <p className="lc-sr" aria-live="polite">
        {monthName} {selectedDate.getDate()}
        {activeYear ? `, ${activeYear}` : ", all years"}
        {loading ? ", loading flights" : `, ${plural(selectedLaunches.length, "flight")}`}
      </p>

      <section className="lc-instrument" ref={stageRef}>
        <div className="lc-wash" aria-hidden="true" />
        <div className="lc-panel-head">
          <div className="lc-title-row">
            <button type="button" className="lc-nav" onClick={() => shiftMonth(-1)} aria-label="Previous month">
              <Chevron direction="left" />
            </button>
            <h1 className="lc-month-title">{monthName}</h1>
            <button type="button" className="lc-nav" onClick={() => shiftMonth(1)} aria-label="Next month">
              <Chevron direction="right" />
            </button>
            <button type="button" className="lc-today" onClick={jumpToday} aria-pressed={onToday}>
              Today
            </button>
          </div>
        </div>

        <div className="lc-record lc-figures">
          <div className="lc-figure">
            <p className="lc-label">Flights</p>
            <strong>
              {loading ? "–" : monthLaunches.length}
              <small>{activeYear ? activeYear : yearsOnPlate ? plural(yearsOnPlate, "year") : "—"}</small>
            </strong>
          </div>
          <div className="lc-figure">
            <p className="lc-label">{activeYear ? "Days flown" : "Years"}</p>
            <strong>
              {loading ? "–" : activeYear ? daysFlown : yearsOnPlate}
              <small>{activeYear ? `of ${daysInMonth}` : "in this month"}</small>
            </strong>
          </div>
          <div className="lc-figure">
            <p className="lc-label">Success</p>
            <strong>
              {loading || successRate == null ? "–" : `${successRate}%`}
              <small>{successRate == null ? "no outcomes yet" : `${outcomeCounts.success} succeeded`}</small>
            </strong>
          </div>
          <button
            type="button"
            className="lc-figure is-button"
            onClick={() => busiestDays[0] && selectDay(new Date(LEAP_ANCHOR, currentMonth, busiestDays[0]))}
            disabled={!busiestDays.length}
          >
            <p className="lc-label">Busiest</p>
            <strong>
              {loading || !busiestDays.length ? "–" : busiestDays[0]}
              <small>
                {maxCount ? `${plural(maxCount, "flight")}${busiestDays.length > 1 ? " · tie" : ""}` : "—"}
              </small>
            </strong>
          </button>
        </div>

        <div className="lc-years" ref={railRef} role="group" aria-label="Year">
          <button
            type="button"
            className={cx("lc-year", "is-all", activeYear == null && "is-active")}
            aria-pressed={activeYear == null}
            onClick={() => selectYear(null)}
          >
            <strong>All</strong>
            <em>{loading ? "–" : monthAll.length}</em>
            <span className="lc-year-bar" />
          </button>
          {yearSpan.map((item) => (
            <button
              type="button"
              key={item.year}
              className={cx(
                "lc-year",
                activeYear === item.year && "is-active",
                item.count === 0 && "is-empty"
              )}
              aria-pressed={activeYear === item.year}
              aria-label={`${item.year}, ${plural(item.count, "flight")} in ${monthName}`}
              onClick={() => selectYear(item.year)}
            >
              <strong>{item.year}</strong>
              <em>{item.count}</em>
              <span className="lc-year-bar" aria-hidden="true">
                {item.count > 0 ? <i style={{ "--h": yearMax ? item.count / yearMax : 0 }} /> : null}
              </span>
            </button>
          ))}
        </div>

        {activeYear != null ? (
          <div className="lc-weekdays" aria-hidden="true">
            {WEEKDAYS.map(([full, short]) => (
              <span key={full}>
                <b>{short}</b>
                <i>{full}</i>
              </span>
            ))}
          </div>
        ) : null}

        <div
          className="lc-grid"
          key={`${currentMonth}-${activeYear ?? "all"}`}
          role="group"
          aria-label={`${monthName} dates`}
        >
          {cells.map((day, index) => {
            if (!day) return <div className="lc-blank" key={`blank-${index}`} />;
            const flights = byDay.get(day.getDate()) || [];
            const count = flights.length;
            const marks = dayMarks(flights, rocketById);
            const mix = marks.map((mark) => `${mark.count} ${VEHICLE_LABEL[mark.kind]}`).join(", ");
            const isSelected = day.getDate() === selectedDate.getDate();
            const isToday =
              day.getMonth() === today.getMonth() &&
              day.getDate() === today.getDate() &&
              (activeYear == null || activeYear === today.getFullYear());

            return (
              <button
                type="button"
                key={day.getDate()}
                className={cx(
                  "lc-day",
                  count > 0 ? "has-flights" : "is-empty",
                  isSelected && "is-selected",
                  isToday && "is-today"
                )}
                aria-pressed={isSelected}
                aria-label={`${format(day, "MMMM d")}${activeYear ? `, ${activeYear}` : ", all years"}, ${plural(count, "flight")}${mix ? `, ${mix}` : ""}`}
                onClick={() => selectDay(day)}
              >
                <span className="lc-day-number">
                  {format(day, "d")}
                  {isToday ? <i className="lc-today-mark" /> : null}
                </span>
                {count > 0 ? <span className="lc-day-count">{count}</span> : null}
                {marks.length ? (
                  <span className="lc-day-pills" aria-hidden="true">
                    {marks.map((mark) => (
                      <span className="lc-day-pill" key={mark.kind} style={{ "--mark": mark.color }}>
                        {VEHICLE_PILL[mark.kind]}
                        {marks.length > 1 && mark.count > 1 ? ` ${mark.count}` : ""}
                      </span>
                    ))}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </section>

      <section className="lc-bottom">
        <aside className="lc-filter-panel">
          {dataLoaded && filter ? (
            <Filter
              filter={filter}
              updateFilter={setFilter}
              filterNames={filterNames}
              objects={objectsData}
              isVisible
            />
          ) : (
            <div className="lc-filter-pending" />
          )}
        </aside>

        <div className="lc-dossier" ref={dossierRef}>
          <div className="lc-record">
            <div>
              <p className="lc-label">{monthName} {selectedDate.getDate()}</p>
              <strong>
                {loading ? "—" : selectedLaunches.length}
                <small>{selectedLaunches.length === 1 ? "flight" : "flights"}</small>
              </strong>
            </div>
            <div>
              <p className="lc-label">Years</p>
              <strong>{loading ? "—" : selectedYears.length || "—"}</strong>
            </div>
            <p>{loading ? "" : yearLine}</p>
          </div>

          {loading ? (
            <div>
              <div className="lc-skel" />
              <div className="lc-skel" />
              <div className="lc-skel" />
            </div>
          ) : (
            <div className="lc-groups" key={`${currentMonth}-${selectedDate.getDate()}-${activeYear ?? "all"}`}>
              {yearGroups.map((group) => (
                <section className="lc-group" key={group.year}>
                  <h2 className="lc-year-head">
                    <strong>{group.year}</strong>
                    <span>{plural(group.flights.length, "flight")}</span>
                  </h2>
                  <ul className="lc-flight-list">
                    {group.flights.map((launch) => {
                      const rocket = rocketById.get(String(launch.rocket));
                      return (
                        <li key={launch.id}>
                          <Link
                            to={`/launch/${launch.id}/`}
                            className="lc-flight"
                            style={{ "--accent": rocketAccent(rocket) }}
                          >
                            <span className="lc-flight-bar" aria-hidden="true" />
                            <span className="lc-flight-copy">
                              <strong className="lc-flight-name">{launch.name}</strong>
                              <span className="lc-flight-meta">{flightMeta(launch) || formatTime(launch.time)}</span>
                            </span>
                            <span className={`lc-outcome ${outcomeClass(launch.launch_outcome)}`}>
                              {outcomeLabel(launch.launch_outcome)}
                            </span>
                            <span className="lc-flight-go" aria-hidden="true">
                              ›
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>
      </section>

      {showIntel ? (
        <section className="lc-intel" ref={intelRef}>
          <div className="lc-intel-wash" aria-hidden="true" />
          <div className="lc-panel-head">
            <p className="lc-label">This month</p>
            <p className="lc-panel-note">
              {plural(monthLaunches.length, "flight")}
              {activeYear ? ` · ${activeYear}` : yearsOnPlate ? ` · ${plural(yearsOnPlate, "year")}` : ""}
            </p>
          </div>

          {monthLaunches.length > 0 ? (
            <div className="lc-intel-grid">
              <div className="lc-intel-col">
                <p className="lc-label">Vehicles</p>
                <div className="lc-mix">
                  {vehicleMix.map((row) => (
                    <div className="lc-mix-row" key={row.name}>
                      <span className="lc-mix-name">{row.name}</span>
                      <span className="lc-mix-count">{row.count}</span>
                      <span className="lc-mix-track">
                        <i style={{ "--w": (row.count / monthLaunches.length) * 100, "--accent": row.color }} />
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lc-intel-col">
                <p className="lc-label">Pads</p>
                <div className="lc-mix">
                  {padMix.map((row) => (
                    <div className="lc-mix-row" key={row.name}>
                      <span className="lc-mix-name">{row.name}</span>
                      <span className="lc-mix-count">{row.count}</span>
                      <span className="lc-mix-track">
                        <i style={{ "--w": (row.count / monthLaunches.length) * 100, "--accent": row.color }} />
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lc-intel-col">
                <p className="lc-label">Outcomes</p>
                <div className="lc-split" aria-hidden="true">
                  {[
                    ["success", outcomeCounts.success],
                    ["partial", outcomeCounts.partial],
                    ["failure", outcomeCounts.failure],
                    ["tbd", outcomeCounts.tbd],
                  ].map(([name, count]) =>
                    count ? (
                      <i key={name} className={name} style={{ width: `${(count / monthLaunches.length) * 100}%` }} />
                    ) : null
                  )}
                </div>
                <ul className="lc-outcome-legend">
                  {[
                    ["success", "Success", outcomeCounts.success],
                    ["partial", "Partial", outcomeCounts.partial],
                    ["failure", "Failure", outcomeCounts.failure],
                    ["tbd", "TBD", outcomeCounts.tbd],
                  ].map(([name, label, count]) => (
                    <li key={name}>
                      <i className={name} />
                      <span>{label}</span>
                      <strong>{count}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}

          {stats.num_days_with_launches != null || stats.days_with_most_launches ? (
            <footer className="lc-record">
              <div>
                <p className="lc-label">Days with a launch</p>
                <strong>
                  {stats.num_days_with_launches ?? "—"}
                  {recordPct != null ? <small>{recordPct}%</small> : null}
                </strong>
              </div>
              <div>
                <p className="lc-label">{String(stats.days_with_most_launches || "").includes("and") ? "Days" : "Day"} with most launches</p>
                <strong>
                  {stats.days_with_most_launches ?? "—"}
                  {stats.most_launches != null ? <small>{plural(stats.most_launches, "flight")}</small> : null}
                </strong>
              </div>
              <p>Across every month in this filter.</p>
            </footer>
          ) : null}
        </section>
      ) : null}
    </div>
  );
};

export default LaunchCalendar;
